if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        if (this.imageLoadTimeout) {
          clearTimeout(this.imageLoadTimeout);
          this.imageLoadTimeout = null;
        }
        
        if (this.heightObserver) {
          this.heightObserver.disconnect();
          this.heightObserver = null;
        }
        
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            const productTitle = productElement.querySelector('.product__title');
            if (productTitle) {
              productTitle.remove();
            }

            const priceContainer = productElement.querySelector('[id^="price-"]');
            if (priceContainer) {
              priceContainer.remove();
            }

            const shippingInfo = productElement.querySelector('.product__predicted-delivery');
            if (shippingInfo) {
              shippingInfo.remove();
            }

            const paymentMethods = productElement.querySelector('.product__payment-methonds');
            if (paymentMethods) {
              paymentMethods.remove();
            }
            
            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            const modalContent = this.querySelector('.quick-add-modal__content');
            if (modalContent) {
              this.originalModalStyles = {
                opacity: modalContent.style.opacity,
                visibility: modalContent.style.visibility
              };
              
              modalContent.style.opacity = '0';
              modalContent.style.visibility = 'hidden';
            }
            
            super.show(opener);

            const modalImages = this.querySelectorAll('.product img, .product .swatch');
            const imageCount = modalImages.length;

            if (imageCount === 0) {
              this.calculateModalHeight(true);
            } else {
              let loadedImages = 0;
              
              const checkAllImagesLoaded = () => {
                loadedImages++;
                
                if (loadedImages >= imageCount) {
                  setTimeout(() => {
                    this.calculateModalHeight(true);
                  }, 10);
                }
              };
              
              this.imageLoadTimeout = setTimeout(() => {
                this.calculateModalHeight(true);
              }, 10);
              
              modalImages.forEach(img => {
                if (img.complete) {
                  checkAllImagesLoaded();
                } else {
                  img.addEventListener('load', checkAllImagesLoaded);
                  img.addEventListener('error', () => {
                    console.log('Image failed to load');
                    checkAllImagesLoaded();
                  });
                }
              });
            }
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      calculateModalHeight(showAfterCalculation = false) {
        if (this.imageLoadTimeout) {
          clearTimeout(this.imageLoadTimeout);
          this.imageLoadTimeout = null;
        }
        
        const quickAddModalContentInfoHeight = this.querySelector('.quick-add-modal__content-info').scrollHeight;
        const viewportHeight = window.innerHeight;

        const modalProductHeight = this.querySelector('.product').scrollHeight;
                  
        const minTopMargin = 30;

        const optimalOffset = Math.max(minTopMargin, viewportHeight - (modalProductHeight + 5));
        
        const remValue = optimalOffset / parseFloat(getComputedStyle(document.documentElement).fontSize);
                
        const modalContent = this.querySelector('.quick-add-modal__content');
        modalContent.style.setProperty('--modal-height-offset', `${remValue}rem`);
        
        // this.setupHeightObserver();
        
        if (showAfterCalculation) {
          setTimeout(() => {
            if (this.originalModalStyles) {
              modalContent.style.opacity = this.originalModalStyles.opacity || '1';
              modalContent.style.visibility = this.originalModalStyles.visibility || 'visible';
            } else {
              modalContent.style.opacity = '1';
              modalContent.style.visibility = 'visible';
            }
            
            const currentTransition = modalContent.style.transition;
            if (!currentTransition || !currentTransition.includes('opacity')) {
              modalContent.style.transition = `${currentTransition ? currentTransition + ', ' : ''}opacity 0.3s ease`;
            }
          }, 10);
        }
      }

      setupHeightObserver() {
        if (this.heightObserver) {
          this.heightObserver.disconnect();
        }
        
        const productElement = this.querySelector('.product');
        const quickAddModalContentInfoHeight = this.querySelector('.quick-add-modal__content-info').scrollHeight;
        const minTopMargin = 30;
        
        this.heightObserver = new MutationObserver(() => {
          setTimeout(() => {
            const newModalProductHeight = productElement.scrollHeight;
            
            const newOptimalOffset = Math.max(minTopMargin, quickAddModalContentInfoHeight - (newModalProductHeight + 10));
            const newRemValue = newOptimalOffset / parseFloat(getComputedStyle(document.documentElement).fontSize);
            
            this.querySelector('.quick-add-modal__content').style.setProperty('--modal-height-offset', `${newRemValue}rem`);
          }, 10);
        });
        
        this.heightObserver.observe(productElement, {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true
        });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
